import React, { useEffect, useState } from "react";
import { Formik, Form as FormikForm } from "formik";
import { Form, Button } from "react-bootstrap";
import * as yup from "yup";
import TimeSlot from "./TimeSlot";
import "./CesReservationForm.css";
import { db } from "../../../firebase/firebase";
import { collection, getDocs, setDoc } from "firebase/firestore";
import { useReservation } from "../context/ReservationContext"; // useReservation hook import
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import TermsModal from "../components/TermsModal";

const countries = [
  "Afghanistan",
  "Albania",
  "Algeria",
  "Andorra",
  "Angola",
  "Antigua and Barbuda",
  "Argentina",
  "Armenia",
  "Australia",
  "Austria",
  "Azerbaijan",
  "Bahamas",
  "Bahrain",
  "Bangladesh",
  "Barbados",
  "Belarus",
  "Belgium",
  "Belize",
  "Benin",
  "Bhutan",
  "Bolivia",
  "Bosnia and Herzegovina",
  "Botswana",
  "Brazil",
  "Brunei",
  "Bulgaria",
  "Burkina Faso",
  "Burundi",
  "Cambodia",
  "Cameroon",
  "Canada",
  "Cape Verde",
  "Central African Republic",
  "Chad",
  "Chile",
  "China",
  "Colombia",
  "Comoros",
  "Congo",
  "Costa Rica",
  "Croatia",
  "Cuba",
  "Cyprus",
  "Czech Republic",
  "Denmark",
  "Djibouti",
  "Dominica",
  "Dominican Republic",
  "East Timor",
  "Ecuador",
  "Egypt",
  "El Salvador",
  "Equatorial Guinea",
  "Eritrea",
  "Estonia",
  "Eswatini",
  "Ethiopia",
  "Fiji",
  "Finland",
  "France",
  "Gabon",
  "Gambia",
  "Georgia",
  "Germany",
  "Ghana",
  "Greece",
  "Grenada",
  "Guatemala",
  "Guinea",
  "Guinea-Bissau",
  "Guyana",
  "Haiti",
  "Honduras",
  "Hong Kong",
  "Hungary",
  "Iceland",
  "India",
  "Indonesia",
  "Iran",
  "Iraq",
  "Ireland",
  "Israel",
  "Italy",
  "Ivory Coast",
  "Jamaica",
  "Japan",
  "Jordan",
  "Kazakhstan",
  "Kenya",
  "Kiribati",
  "Kuwait",
  "Kyrgyzstan",
  "Laos",
  "Latvia",
  "Lebanon",
  "Lesotho",
  "Liberia",
  "Libya",
  "Liechtenstein",
  "Lithuania",
  "Luxembourg",
  "Madagascar",
  "Malawi",
  "Malaysia",
  "Maldives",
  "Mali",
  "Malta",
  "Marshall Islands",
  "Mauritania",
  "Mauritius",
  "Mexico",
  "Micronesia",
  "Moldova",
  "Monaco",
  "Mongolia",
  "Montenegro",
  "Morocco",
  "Mozambique",
  "Myanmar",
  "Namibia",
  "Nauru",
  "Nepal",
  "Netherlands",
  "New Zealand",
  "Nicaragua",
  "Niger",
  "Nigeria",
  "North Korea",
  "North Macedonia",
  "Norway",
  "Oman",
  "Pakistan",
  "Palau",
  "Palestine",
  "Panama",
  "Papua New Guinea",
  "Paraguay",
  "Peru",
  "Philippines",
  "Poland",
  "Portugal",
  "Qatar",
  "Romania",
  "Russia",
  "Rwanda",
  "Saint Kitts and Nevis",
  "Saint Lucia",
  "Saint Vincent and the Grenadines",
  "Samoa",
  "San Marino",
  "Sao Tome and Principe",
  "Saudi Arabia",
  "Senegal",
  "Serbia",
  "Seychelles",
  "Sierra Leone",
  "Singapore",
  "Slovakia",
  "Slovenia",
  "Solomon Islands",
  "Somalia",
  "South Africa",
  "South Korea",
  "South Sudan",
  "Spain",
  "Sri Lanka",
  "Sudan",
  "Suriname",
  "Sweden",
  "Switzerland",
  "Syria",
  "Taiwan",
  "Tajikistan",
  "Tanzania",
  "Thailand",
  "Togo",
  "Tonga",
  "Trinidad and Tobago",
  "Tunisia",
  "Turkey",
  "Turkmenistan",
  "Tuvalu",
  "Uganda",
  "Ukraine",
  "United Arab Emirates",
  "United Kingdom",
  "United States",
  "Uruguay",
  "Uzbekistan",
  "Vanuatu",
  "Vatican City",
  "Venezuela",
  "Vietnam",
  "Yemen",
  "Zambia",
  "Zimbabwe",
].sort();

const schema = yup.object().shape({
  fullName: yup
    .string()
    .required("Please enter your full name")
    .min(2, "Name must be at least 2 characters"),
  company: yup
    .string()
    .required("Please enter your company name")
    .min(2, "Company name must be at least 2 characters"),
  country: yup.string().required("Please select your country"),
  email: yup
    .string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Please enter a valid email (e.g., name@domain.com)",
    )
    .test("email-format", "Email must contain @ symbol", (value) => {
      return value && value.includes("@");
    })
    .test("email-domain", "Email must have a valid domain", (value) => {
      if (!value) return false;
      const parts = value.split("@");
      return parts.length === 2 && parts[1].includes(".");
    })
    .required("Email is required"),
  phone: yup
    .string()
    .matches(/^[0-9+\-() ]+$/, "Please enter a valid phone number")
    .required("Please enter your phone number"),
  message: yup.string(),
  time: yup.string(),
  terms: yup
    .bool()
    .oneOf([true], "You must agree to the terms")
    .required("You must agree to the terms"),
});

export default function CesReservationForm() {
  const { timeSlots, submitReservation, isSubmitting } = useReservation();
  const navigate = useNavigate();
  const [showTerms, setShowTerms] = useState(false);

  const onSubmit = async (values, { setSubmitting, resetForm }) => {
    const result = await submitReservation(values);

    if (result.success) {
      toast.success(result.message);
      resetForm();
      navigate("/ces"); // 예약 완료 후 CES 랜딩 페이지로 이동
    } else {
      toast.error(result.message);
    }

    setSubmitting(false);
  };

  return (
    <div className="main">
      <div className="main__card">
        <div className="card__header">
          <h2 id="card__header-title">CES 2025 Meeting Reservation</h2>
          <p id="card__header-subtitle">
            January 7-10, 2025 | Las Vegas Convention Center
          </p>
        </div>
        <div className="form__container">
          <Formik
            initialValues={{
              fullName: "",
              company: "",
              country: "",
              email: "",
              phone: "",
              message: "",
              //time: "",
              terms: false,
            }}
            // validateOnChange={false}
            validateOnBlur={false}
            validationSchema={schema}
            onSubmit={onSubmit}
          >
            {({
              handleSubmit,
              handleChange,
              values,
              touched,
              errors,
              setFieldValue,
              handleBlur,
            }) => (
              <FormikForm
                noValidate
                onSubmit={handleSubmit}
                className="reservation-form"
              >
                <Form.Group md="12" controlId="validationFormik01">
                  <Form.Label className="form__label">Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="fullName"
                    value={values.fullName}
                    onChange={handleChange}
                    isValid={touched.fullName && !errors.fullName}
                    isInvalid={touched.fullName && errors.fullName}
                    placeholder="Enter your full name"
                    lang="en"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.fullName}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group md="12" controlId="validationFormik02">
                  <Form.Label className="form__label">Company *</Form.Label>
                  <Form.Control
                    type="text"
                    name="company"
                    value={values.company}
                    onChange={handleChange}
                    isValid={touched.company && !errors.company}
                    isInvalid={touched.company && errors.company}
                    placeholder="Enter your company name"
                    lang="en"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.company}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group md="12" controlId="validationFormik03">
                  <Form.Label className="form__label">Country *</Form.Label>
                  <Form.Select
                    name="country"
                    value={values.country}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    isInvalid={touched.country && errors.country}
                  >
                    <option value="">Select your country</option>
                    {countries.map((country) => (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.country}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group md="12" controlId="validationFormik04">
                  <Form.Label className="form__label">Email *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    isValid={touched.email && !errors.email}
                    isInvalid={touched.email && errors.email}
                    placeholder="Enter your email"
                    lang="en"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.email}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group md="12" controlId="validationFormik05">
                  <Form.Label className="form__label">Phone *</Form.Label>
                  <Form.Control
                    type="text"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    isValid={touched.phone && !errors.phone}
                    isInvalid={touched.phone && errors.phone}
                    placeholder="Enter your phone number"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.phone}
                  </Form.Control.Feedback>
                </Form.Group>

                <Form.Group md="12" controlId="validationFormik06">
                  <Form.Label className="form__label">Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={1}
                    name="message"
                    value={values.message}
                    onChange={handleChange}
                    placeholder="Enter additional messages (optional)"
                  />
                </Form.Group>

                <Form.Group md="12" controlId="validationFormik08">
                  <Form.Label className="form__label">
                    Preferred Time *
                  </Form.Label>
                  <TimeSlot timeSlots={timeSlots} />
                </Form.Group>

                <Form.Group className="mb-3 form__label" id="terms-label">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Form.Check
                      required
                      name="terms"
                      onChange={handleChange}
                      isInvalid={touched.terms && errors.terms}
                      feedback={errors.terms}
                      id="validationFormik09"
                      lang="en"
                    />
                    <span
                      style={{
                        marginLeft: "8px",
                        color: "#d32f2f",
                        cursor: "pointer",
                      }}
                    >
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowTerms(true);
                        }}
                        style={{
                          color: "#d32f2f",
                          textDecoration: "underline",
                        }}
                      >
                        I agree to the terms and conditions
                      </a>{" "}
                      *
                    </span>
                  </div>
                </Form.Group>

                <div className="button-container">
                  <Button type="submit" id="btn_submit" disabled={isSubmitting}>
                    Submit Reservation
                  </Button>
                </div>
              </FormikForm>
            )}
          </Formik>
        </div>
      </div>
      <TermsModal show={showTerms} onHide={() => setShowTerms(false)} />
    </div>
  );
}
